import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Phone } from 'lucide-react';
import { ContentContext } from '../App';

const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const content = useContext(ContentContext);
  const company = content?.company || {};
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const isHomePage = location.pathname === '/';

  // Custom nav labels for blog
  const navLabels = {
    ...t.nav,
    blog: language === 'en' ? 'Blog' : '博客'
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'services', href: '#services' },
    { key: 'projects', href: '#projects' },
    { key: 'testimonials', href: '#testimonials' },
    { key: 'blog', href: '/blog', isRoute: true },
    { key: 'contact', href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (!isHomePage) {
        // Navigate to home first, then scroll
        window.location.href = '/' + href;
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
              <span className="text-slate-900 font-black text-lg">R</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              ROOF<span className="text-amber-400">TOPPER</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.isRoute ? (
                <Link
                  key={item.key}
                  to={item.href}
                  className="text-slate-300 hover:text-amber-400 font-medium transition-colors relative group"
                >
                  {navLabels[item.key]}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 transition-all group-hover:w-full" />
                </Link>
              ) : (
                <a
                  key={item.key}
                  href={isHomePage ? item.href : '/' + item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="text-slate-300 hover:text-amber-400 font-medium transition-colors relative group"
                >
                  {navLabels[item.key]}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 transition-all group-hover:w-full" />
                </a>
              )
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center bg-slate-800/50 rounded-full p-1">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => language !== 'zh' && toggleLanguage()}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  language === 'zh' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                中文
              </button>
            </div>
            <a href={`tel:${company.phone || '416-688-9988'}`}>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Phone className="w-4 h-4 mr-2" />
                {company.phone || '416-688-9988'}
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="flex items-center bg-slate-800/50 rounded-full p-0.5">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => language !== 'zh' && toggleLanguage()}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'zh' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400'
                }`}
              >
                中文
              </button>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-slate-900 border-slate-800">
                <nav className="flex flex-col gap-4 mt-8">
                  {navItems.map((item) => (
                    item.isRoute ? (
                      <Link
                        key={item.key}
                        to={item.href}
                        className="text-slate-300 hover:text-amber-400 font-medium text-lg py-2 transition-colors"
                      >
                        {navLabels[item.key]}
                      </Link>
                    ) : (
                      <a
                        key={item.key}
                        href={isHomePage ? item.href : '/' + item.href}
                        onClick={(e) => scrollToSection(e, item.href)}
                        className="text-slate-300 hover:text-amber-400 font-medium text-lg py-2 transition-colors"
                      >
                        {navLabels[item.key]}
                      </a>
                    )
                  ))}
                  <a href={`tel:${company.phone || '416-688-9988'}`} className="mt-4">
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                      <Phone className="w-4 h-4 mr-2" />
                      {company.phone || '416-688-9988'}
                    </Button>
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
